import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

declare var DBSService_BASE_URL: string;

// ============================================
// Interfaces
// ============================================

export interface AssignmentItem {
  id?: number;
  productlineId: number;
  processId: number;
  typeGroupId?: number | null;
  // Nav properties (read-only from API)
  process?: { id: number; name: string };
  productLine?: { id: number; name: string };
  typeGroup?: { id: number; name: string } | null;
  resourceAssignments?: ResourceAssignmentItem[];
}

export interface ResourceItem {
  id?: number;
  userId: number;
  isActive: boolean;
  user?: { id: number; displayName: string; email: string; loginID: string };
}

export interface ResourceAssignmentItem {
  id?: number;
  resourceId: number;
  assignmentId: number;
  isMain: boolean;
  resource?: ResourceItem;
}

// ============================================
// Service
// ============================================

@Injectable({
  providedIn: 'root'
})
export class AssignmentService {
  private get baseUrl(): string {
    return `${DBSService_BASE_URL}/api/Assignment`;
  }

  constructor(private http: HttpClient) {}

  // ---- Assignments ----

  getAssignments(): Observable<AssignmentItem[]> {
    return this.http.get<AssignmentItem[]>(this.baseUrl);
  }

  getAssignment(id: number): Observable<AssignmentItem> {
    return this.http.get<AssignmentItem>(`${this.baseUrl}/${id}`);
  }

  createAssignment(assignment: Partial<AssignmentItem>): Observable<AssignmentItem> {
    return this.http.post<AssignmentItem>(this.baseUrl, assignment);
  }

  updateAssignment(id: number, assignment: Partial<AssignmentItem>): Observable<AssignmentItem> {
    return this.http.put<AssignmentItem>(`${this.baseUrl}/${id}`, assignment);
  }

  deleteAssignment(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  // ---- Resources ----

  getResources(): Observable<ResourceItem[]> {
    return this.http.get<ResourceItem[]>(`${this.baseUrl}/Resources`);
  }

  createResource(resource: Partial<ResourceItem>): Observable<ResourceItem> {
    return this.http.post<ResourceItem>(`${this.baseUrl}/Resources`, resource);
  }

  updateResource(id: number, resource: Partial<ResourceItem>): Observable<ResourceItem> {
    return this.http.put<ResourceItem>(`${this.baseUrl}/Resources/${id}`, resource);
  }

  deleteResource(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/Resources/${id}`);
  }

  // ---- Resource Assignments ----

  getResourceAssignments(assignmentId: number): Observable<ResourceAssignmentItem[]> {
    return this.http.get<ResourceAssignmentItem[]>(`${this.baseUrl}/${assignmentId}/ResourceAssignments`);
  }

  addResourceToAssignment(assignmentId: number, resourceId: number, isMain: boolean): Observable<ResourceAssignmentItem> {
    return this.http.post<ResourceAssignmentItem>(`${this.baseUrl}/${assignmentId}/ResourceAssignments`, { resourceId, isMain });
  }

  updateResourceAssignment(id: number, isMain: boolean): Observable<ResourceAssignmentItem> {
    return this.http.put<ResourceAssignmentItem>(`${this.baseUrl}/ResourceAssignments/${id}`, { isMain });
  }

  removeResourceFromAssignment(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/ResourceAssignments/${id}`);
  }
}
